import { Eye, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage } from '@/api/client';
import { useDeleteReport, useDestinations, useReports, useSaveReport, useSendReport } from '@/api/hooks';
import type { Report } from '@/api/types';
import { ConfirmDialog } from '@/components/Dialog';
import { Menu } from '@/components/Menu';
import { useToast } from '@/components/toast-context';
import { Badge, Button, ButtonLink, Card, EmptyState, ErrorNote, PageHeader, Skeleton, Switch } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import { COVERS, scheduleSummary } from './draft';
import { PreviewDialog } from './PreviewDialog';

function ReportRow({ report: r, destinationNames, onPreview, onDelete }: { report: Report; destinationNames: string[]; onPreview: () => void; onDelete: () => void }) {
  const save = useSaveReport();
  const send = useSendReport();
  const toast = useToast();
  const navigate = useNavigate();

  return (
    <Card as="li" className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-medium">{r.name}</h2>
          {!r.enabled && <Badge>Paused</Badge>}
        </div>
        <p className="text-sm text-muted-ink">
          {scheduleSummary(r)} · {r.timezone}
        </p>
        <p className="mt-1 text-xs text-muted-ink">
          {r.widget_ids.length === 0 ? 'Every KPI' : `${r.widget_ids.length} KPI${r.widget_ids.length === 1 ? '' : 's'}`} →{' '}
          {destinationNames.length ? destinationNames.join(', ') : <span className="text-down">no destination</span>}
        </p>
        <p className="mt-1 text-xs text-muted-ink">
          {r.enabled && r.next_run_at ? `Next: ${formatDateTime(r.next_run_at, r.timezone)}` : 'Not scheduled'}
          {r.last_sent_at ? ` · Last sent ${formatDateTime(r.last_sent_at, r.timezone)}` : ''}
        </p>
        <p className="sr-only">{COVERS[r.frequency]}</p>
      </div>
      <div className="flex items-center gap-2">
        <Switch
          label={`Send “${r.name}”`}
          hideLabel
          checked={r.enabled}
          disabled={save.isPending}
          onChange={(enabled) =>
            save.mutate(
              { id: r.id, input: { enabled } },
              {
                onSuccess: () => toast({ title: enabled ? `“${r.name}” is on` : `“${r.name}” is paused` }),
                onError: (e) => toast({ tone: 'error', title: 'Couldn’t change it', description: errorMessage(e) }),
              },
            )
          }
        />
        <Button size="sm" onClick={onPreview} icon={<Eye className="size-4" aria-hidden />}>
          Preview
        </Button>
        <Menu
          label={`More actions for ${r.name}`}
          items={[
            {
              label: 'Send now',
              icon: <Send className="size-4" aria-hidden />,
              disabled: r.destination_ids.length === 0,
              onSelect: () =>
                send.mutate(r.id, {
                  onSuccess: () => toast({ title: 'Sending now', description: `“${r.name}” is on its way to ${destinationNames.join(', ') || 'its destinations'}.` }),
                  onError: (e) => toast({ tone: 'error', title: 'Couldn’t send it', description: errorMessage(e) }),
                }),
            },
            { label: 'Edit', icon: <Pencil className="size-4" aria-hidden />, onSelect: () => navigate(`/updates/${r.id}`) },
            { label: 'Delete', icon: <Trash2 className="size-4" aria-hidden />, danger: true, onSelect: onDelete },
          ]}
        />
      </div>
    </Card>
  );
}

export function ReportsPage() {
  const reports = useReports();
  const destinations = useDestinations();
  const remove = useDeleteReport();
  const toast = useToast();
  const [preview, setPreview] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Report | null>(null);
  const destName = new Map(destinations.data?.map((d) => [d.id, d.label]) ?? []);

  return (
    <div>
      <PageHeader
        title="Updates"
        description="Get your numbers sent to you daily, weekly or monthly."
        actions={
          <ButtonLink to="/updates/new" variant="primary" icon={<Plus className="size-4" aria-hidden />}>
            New update
          </ButtonLink>
        }
      />
      {reports.isError && <ErrorNote message={errorMessage(reports.error)} onRetry={() => void reports.refetch()} />}
      {reports.isPending && <Skeleton className="h-28" />}
      {reports.data?.length === 0 && (
        <EmptyState
          icon={<Send className="size-5" aria-hidden />}
          title="No updates scheduled"
          actions={
            <ButtonLink to="/updates/new" variant="primary">
              Schedule your first update
            </ButtonLink>
          }
        >
          For example: every morning at 8, yesterday’s sales and followers to your email and Slack.
        </EmptyState>
      )}
      {reports.data && reports.data.length > 0 && (
        <ul className="flex flex-col gap-3">
          {reports.data.map((r) => (
            <ReportRow
              key={r.id}
              report={r}
              destinationNames={r.destination_ids.map((id) => destName.get(id) ?? 'Removed destination')}
              onPreview={() => setPreview(r.id)}
              onDelete={() => setDeleting(r)}
            />
          ))}
        </ul>
      )}
      <PreviewDialog reportId={preview} onClose={() => setPreview(null)} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title={`Delete “${deleting?.name ?? ''}”?`}
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => {
          const r = deleting;
          if (!r) return;
          remove.mutate(r.id, {
            onSuccess: () => {
              toast({ title: `Deleted “${r.name}”` });
              setDeleting(null);
            },
            onError: (e) => toast({ tone: 'error', title: 'Couldn’t delete it', description: errorMessage(e) }),
          });
        }}
      >
        <p>It won’t be sent again. Your KPIs and destinations stay as they are.</p>
      </ConfirmDialog>
    </div>
  );
}
